import * as PIXI from "pixi.js";
import { symbolData, isCoinSymbol } from "../../../SymbolData";
import { ASSET_NAME } from "../../../assets";
import {
  formatToCustomLocale,
  attemptFullscreenToggle,
} from "../../../utilities";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import store from "../../../State/Store/store";
import {
  openSymbolPopup,
  closeSymbolPopup,
  closeMenu,
} from "../../../State/actions";
import { PixiSettings } from "../../../pixiSettings";
import { Settings } from "../../../settings";

// Visible sprites start after top buffer (index 1) + landing zone (N symbols)
// Matches ReelControllerV2.js calculation: 1 + Settings.SYMBOLS_VISIBLE
const VISIBLE_SYMBOL_START_INDEX = 1 + Settings.SYMBOLS_VISIBLE;

// Configuration constants for info popup positioning, sizing, and styling
const INFO_SYMBOL_POPUP_CONFIG = {
  // Font sizes
  PAYTABLE_FONT_SIZE: 26,
  DESCRIPTION_FONT_SIZE: 19,
  LINE_HEIGHT_MULTIPLIER: 1.1,
  CONTENT_PADDING: 0.15,

  // Base dimensions before texture scaling
  POPUP_WIDTH_MULTIPLIER: 2.4,
  POPUP_HEIGHT_MULTIPLIER: 1.3,

  // Frame scaling (applied to popup dimensions)
  FRAME_WIDTH_SCALE: 1.2,
  FRAME_HEIGHT_SCALE: 1.45,

  // Positioning
  SYMBOL_Y_OFFSET_MULTIPLIER: -(0.5 * Settings.SYMBOLS_VISIBLE - 0.5), // Vertical center offset — matches OverlayReelSet formula

  // Coin value text (BitmapText)
  COIN_VALUE_FONT_SIZE_MULTIPLIER: 0.25, // fontSize = symbolHeight * this
  COIN_VALUE_MAX_WIDTH_MULTIPLIER: 0.9, // maxWidth = symbolHeight * this

  // Text container sizing
  TEXT_CONTAINER_WIDTH_MULTIPLIER: 0.9, // Increased to allow more width for descriptions
  TEXT_START_X_OFFSET_MULTIPLIER_LEFT: 0.5, // X offset multiplier for left side popups
  TEXT_START_X_OFFSET_MULTIPLIER_RIGHT: 0.5, // X offset multiplier for right side popups

  // Text spacing
  TEXT_GAP_MULTIPLIER: 0.3, // gap = fontSize * this

  // Texture offsets (for left/right side positioning)
  POPUP_TEXTURE_X_OFFSET_LEFT: -0.15,
  POPUP_TEXTURE_Y_OFFSET_LEFT: -0.125,
  POPUP_TEXTURE_X_OFFSET_RIGHT: 0.15,
  POPUP_TEXTURE_Y_OFFSET_RIGHT: -0.125,
};

/**
 * InfoOverlayController - Manages symbol info popups dynamically
 * Creates popup sprites on-demand when symbols are clicked
 * Only one popup can be active at a time
 */
class InfoOverlayController {
  constructor(
    overlayContainer,
    dimensions,
    reelRefs,
    t, // i18n translation function
    bet, // Current bet value
    overlayReelRefs, // Overlay reel refs for checking expanding wild state
  ) {
    this.overlayContainer = overlayContainer;
    this.dimensions = dimensions;
    this.reelRefs = reelRefs;
    this.overlayReelRefs = overlayReelRefs;
    this.t = t;
    this.bet = bet || 1; // Store bet value
    this.baseBet = 1; // Base bet for paytable calculations (will be updated via updateBaseBet())

    // Active popup state
    this.activePopup = null;
    this.activeSymbolPosition = null; // "reelIndex-symbolIndex"

    // Clickable areas (always present, lightweight)
    this.clickableAreas = [];

    // Cached textures
    this.infoFrameTexture = PIXI.Assets.get(ASSET_NAME.info_frame);

    // Track device type for desktop-specific behavior
    this.device = dimensions.device;

    // Internal caches for performance optimization
    this._symbolTypeCache = new Map(); // Cache symbol type lookups
    this._paytableScaleFactorCache = new Map(); // Cache paytable scale factor calculations
    this._framePositionCache = null; // Cache frame position calculations
    this._lastFrameDimensions = null; // Track dimensions for frame cache invalidation

    // Create clickable areas (REEL_COUNT × SYMBOLS_VISIBLE)
    this._createClickableAreas();
  }

  /**
   * Creates invisible clickable sprites over each symbol position
   * Creates clickable areas dynamically based on Settings (REEL_COUNT × SYMBOLS_VISIBLE)
   */
  _createClickableAreas() {
    const { symbolWidth, symbolHeight, reelSpacing } = this.dimensions;

    for (let reelIndex = 0; reelIndex < Settings.REEL_COUNT; reelIndex++) {
      for (
        let symbolIndex = 0;
        symbolIndex < Settings.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        const clickArea = new PIXI.Sprite(PIXI.Texture.EMPTY);
        clickArea.width = symbolWidth;
        clickArea.height = symbolHeight;
        clickArea.anchor.set(0.5);
        clickArea.alpha = 0.01; // Nearly invisible but still interactive
        clickArea.eventMode = "static";
        clickArea.cursor = "pointer";
        clickArea.zIndex = 10; // Above overlay background

        // Position based on reel grid (matches InfoReel positioning)
        clickArea.x = Math.round(reelSpacing * (reelIndex - 2));
        clickArea.y = Math.round(
          symbolHeight * symbolIndex +
            symbolHeight * INFO_SYMBOL_POPUP_CONFIG.SYMBOL_Y_OFFSET_MULTIPLIER,
        );

        // Store indices for click handler
        clickArea.reelIndex = reelIndex;
        clickArea.symbolIndex = symbolIndex;

        clickArea.on("pointerdown", (event) => {
          this.handleSymbolClick(reelIndex, symbolIndex);
          event.stopPropagation();
        });

        this.clickableAreas.push(clickArea);
        this.overlayContainer.addChild(clickArea);
      }
    }
  }

  /**
   * Handles symbol click
   * Desktop: Always opens popup (can switch between symbols)
   * Mobile/Tablet: If popup is open, close it. Otherwise open it.
   */
  handleSymbolClick(reelIndex, symbolIndex) {
    // Close menu if open
    store.dispatch(closeMenu());

    // Attempt to toggle fullscreen if conditions are met
    attemptFullscreenToggle(store.dispatch, this.device);

    soundManager.play(SOUND_NAME.button_click);

    // On mobile/tablet: if any popup is open, just close it
    if (!this.isDesktop() && this.activePopup) {
      this.closePopup();
      return;
    }

    // Desktop or no popup open: open the popup
    this.openPopup(reelIndex, symbolIndex);
  }

  /**
   * Opens popup - creates all sprites dynamically on-demand
   * Creates frame sprite, symbol texture, coin value text (if coin), and paytable/description text
   */
  openPopup(reelIndex, symbolIndex) {
    // Close existing popup first
    this.closePopup();

    const positionId = `${reelIndex}-${symbolIndex}`;
    this.activeSymbolPosition = positionId;

    // Get symbol data from reel controller
    const reelSymbolData = this._getSymbolDataFromReel(reelIndex, symbolIndex);

    // Check if we have valid symbol data
    // symbolId can be 0 (valid), so check explicitly for null/undefined
    // Also verify the symbol exists in SymbolData and has a texture
    const hasValidSymbolId =
      reelSymbolData.symbolId !== null && reelSymbolData.symbolId !== undefined;
    const symbolExists =
      hasValidSymbolId && symbolData[reelSymbolData.symbolId];
    const hasTexture =
      symbolExists && symbolData[reelSymbolData.symbolId].texture;

    if (
      !reelSymbolData.texture ||
      !hasValidSymbolId ||
      !symbolExists ||
      !hasTexture
    ) {
      return; // Can't create popup without data
    }

    // Create popup container
    const popupContainer = new PIXI.Container();
    popupContainer.name = "active_info_popup";
    popupContainer.sortableChildren = true;
    popupContainer.zIndex = 20; // Above clickable areas

    // Position popup container at symbol position
    const { symbolWidth, symbolHeight, reelSpacing } = this.dimensions;
    popupContainer.x = Math.round(reelSpacing * (reelIndex - 2));
    popupContainer.y = Math.round(
      symbolHeight * symbolIndex +
        symbolHeight * INFO_SYMBOL_POPUP_CONFIG.SYMBOL_Y_OFFSET_MULTIPLIER,
    );

    // Calculate dimensions
    const popupWidth =
      symbolWidth * INFO_SYMBOL_POPUP_CONFIG.POPUP_WIDTH_MULTIPLIER;
    const popupHeight =
      symbolHeight * INFO_SYMBOL_POPUP_CONFIG.POPUP_HEIGHT_MULTIPLIER;
    const isLeftSide = reelIndex < 3;

    // Set up container for desktop hover detection - match stone frame dimensions and position
    if (this.isDesktop()) {
      // Use the base popup dimensions to match the stone frame content area
      const hitWidth = popupWidth;
      const hitHeight = popupHeight;

      // Calculate frame position offset (same as frameSprite positioning)
      let popupX;
      if (isLeftSide) {
        popupX = -symbolWidth / 2 - this.dimensions.dividerDimensions.width;
      } else {
        popupX =
          symbolWidth / 2 -
          popupWidth +
          this.dimensions.dividerDimensions.width;
      }

      const frameXOffset = Math.round(
        popupX +
          popupWidth / 2 +
          symbolWidth *
            (isLeftSide
              ? INFO_SYMBOL_POPUP_CONFIG.POPUP_TEXTURE_X_OFFSET_LEFT
              : INFO_SYMBOL_POPUP_CONFIG.POPUP_TEXTURE_X_OFFSET_RIGHT),
      );

      const frameYOffset = Math.round(
        -symbolHeight / 2 -
          this.dimensions.dividerDimensions.width +
          popupHeight / 2 +
          symbolHeight *
            (isLeftSide
              ? INFO_SYMBOL_POPUP_CONFIG.POPUP_TEXTURE_Y_OFFSET_LEFT
              : INFO_SYMBOL_POPUP_CONFIG.POPUP_TEXTURE_Y_OFFSET_RIGHT),
      );

      popupContainer.eventMode = "static";
      popupContainer.hitArea = new PIXI.Rectangle(
        frameXOffset - hitWidth / 2,
        frameYOffset - hitHeight / 2,
        hitWidth,
        hitHeight,
      );

      popupContainer.on("pointerleave", () => {
        this.closePopup();
      });
    } else {
      popupContainer.eventMode = "none";
    }

    // Create frame sprite
    const frameSprite = this._createFrameSprite(
      popupWidth,
      popupHeight,
      isLeftSide,
    );
    popupContainer.addChild(frameSprite);

    // Create symbol texture copy
    const symbolSprite = new PIXI.Sprite(reelSymbolData.texture);
    symbolSprite.anchor.set(0.5);
    symbolSprite.scale.set(reelSymbolData.scale);
    symbolSprite.x = 0;
    symbolSprite.y = 0;
    symbolSprite.zIndex = 20;
    symbolSprite.name = "info_symbol_texture";
    popupContainer.addChild(symbolSprite);

    // Create coin value text if it's a coin symbol
    // Use the actual value from the reel (frozen) instead of recalculating with current bet
    // Matches ReelControllerV2.js scaling logic exactly for visual consistency
    const symbolInfo = symbolData[reelSymbolData.symbolId];
    let coinValueText = null;
    if (symbolInfo?.value && reelSymbolData.coinValueText) {
      // Use the frozen coin value from the reel
      const displayValue = reelSymbolData.coinValueText;
      if (displayValue) {
        const { symbolHeight, symbolWidth } = this.dimensions;
        const fontSize = Math.round(
          symbolHeight *
            INFO_SYMBOL_POPUP_CONFIG.COIN_VALUE_FONT_SIZE_MULTIPLIER,
        );
        coinValueText = new PIXI.BitmapText(displayValue, {
          fontName: "font",
          fontSize: fontSize,
        });
        coinValueText.anchor.set(0.5);
        coinValueText.name = "coin_value";
        coinValueText.zIndex = 21; // Above symbol sprite
        coinValueText.x = 0;
        coinValueText.y = 0;

        // Apply ReelControllerV2.js scaling logic (matches spinning reels exactly)
        // Reset scale first
        coinValueText.scale.set(1);

        // Force update to get accurate measurements
        coinValueText.updateText();

        // Calculate available width (with padding - matches ReelControllerV2.js)
        const coinWidth = symbolWidth;
        const paddingPercent = 0.12; // 12% padding on each side (same as ReelControllerV2.js)
        const maxTextWidth = coinWidth * (1 - paddingPercent * 2);

        const actualTextWidth = coinValueText.width;

        // Only apply scaling if text is too wide
        if (actualTextWidth > maxTextWidth) {
          // Count digits (excluding formatting characters like commas and dots)
          const digitCount = displayValue.replace(/[^0-9]/g, "").length;

          // Apply scaling method based on digit count (matches ReelControllerV2.js)
          if (digitCount <= 4) {
            // 1-4 digits: Horizontal squishing only
            const scaleX = maxTextWidth / actualTextWidth;
            coinValueText.scale.x = scaleX;
          } else if (digitCount >= 5 && digitCount <= 8) {
            // 5-8 digits: Horizontal squishing only (preserve height)
            const scaleX = maxTextWidth / actualTextWidth;
            coinValueText.scale.x = scaleX;
          } else {
            // 9+ digits: Uniform scaling (both width and height)
            const scale = maxTextWidth / actualTextWidth;
            coinValueText.scale.set(scale);
          }
        }

        popupContainer.addChild(coinValueText);
      }
    }

    // Create text content
    const textElements = this._createTextContent(
      reelSymbolData.symbolId,
      isLeftSide,
      popupWidth,
      popupHeight,
    );
    textElements.forEach((el) => popupContainer.addChild(el));

    // Store reference for cleanup and bet updates
    this.activePopup = {
      container: popupContainer,
      frameSprite,
      symbolSprite,
      coinValueText,
      textElements,
      reelIndex,
      symbolIndex,
      symbolId: reelSymbolData.symbolId,
    };

    this.overlayContainer.addChild(popupContainer);

    // Dispatch Redux action
    store.dispatch(openSymbolPopup(positionId));
  }

  /**
   * Closes popup - destroys all dynamically created sprites
   * Cleans up frame, symbol texture, coin value text, and paytable text elements
   */
  closePopup() {
    if (!this.activePopup) {
      return;
    }

    // Remove from overlay
    this.overlayContainer.removeChild(this.activePopup.container);

    // Destroy all sprites
    this.activePopup.frameSprite.destroy({ children: true });
    this.activePopup.symbolSprite.destroy();
    if (this.activePopup.coinValueText) {
      this.activePopup.coinValueText.destroy();
    }
    this.activePopup.textElements.forEach((el) => el.destroy());
    this.activePopup.container.destroy({ children: true });

    this.activePopup = null;
    this.activeSymbolPosition = null;

    // Dispatch Redux action
    store.dispatch(closeSymbolPopup());
  }

  /**
   * Creates the decorative frame sprite for the popup
   * Positions frame on left or right side based on reel position
   */
  _createFrameSprite(popupWidth, popupHeight, isLeftSide) {
    const { symbolWidth, symbolHeight, dividerDimensions } = this.dimensions;

    const frameSprite = new PIXI.Sprite(this.infoFrameTexture);
    frameSprite.anchor.set(0.5);
    frameSprite.width = popupWidth * INFO_SYMBOL_POPUP_CONFIG.FRAME_WIDTH_SCALE;
    frameSprite.height =
      popupHeight * INFO_SYMBOL_POPUP_CONFIG.FRAME_HEIGHT_SCALE;
    frameSprite.name = "info_popup_visual";

    // Calculate frame position (matches InfoSymbolPopup logic)
    let popupX;
    if (isLeftSide) {
      popupX = -symbolWidth / 2 - dividerDimensions.width;
    } else {
      popupX = symbolWidth / 2 - popupWidth + dividerDimensions.width;
    }

    frameSprite.x = Math.round(
      popupX +
        popupWidth / 2 +
        symbolWidth *
          (isLeftSide
            ? INFO_SYMBOL_POPUP_CONFIG.POPUP_TEXTURE_X_OFFSET_LEFT
            : INFO_SYMBOL_POPUP_CONFIG.POPUP_TEXTURE_X_OFFSET_RIGHT),
    );

    frameSprite.y = Math.round(
      -symbolHeight / 2 -
        dividerDimensions.width +
        popupHeight / 2 +
        symbolHeight *
          (isLeftSide
            ? INFO_SYMBOL_POPUP_CONFIG.POPUP_TEXTURE_Y_OFFSET_LEFT
            : INFO_SYMBOL_POPUP_CONFIG.POPUP_TEXTURE_Y_OFFSET_RIGHT),
    );

    return frameSprite;
  }

  /**
   * Gets symbol texture, scale, and ID from Redux state and rendered sprite
   * If expanding wild is active on this reel, returns wild symbol instead
   * Uses currentRound.reels from Redux state
   * Retrieves actual texture and scale from ReelControllerV2's rendered sprite
   */
  _getSymbolDataFromReel(reelIndex, symbolIndex) {
    // Check if expanding wild is active on this reel
    const overlayReel = this.overlayReelRefs?.current?.[reelIndex];
    const overlayController = overlayReel?.getOverlayReelController?.();

    // If expanding wild is visible, return wild symbol (ID 1)
    if (overlayController?.isExpandingWildVisible) {
      const WILD_SYMBOL_ID = 1;
      const symbolInfo = symbolData[WILD_SYMBOL_ID];

      if (!symbolInfo?.texture) {
        return { texture: null, scale: 1, symbolId: WILD_SYMBOL_ID };
      }

      // Get wild texture directly from symbolData
      const textureName = symbolInfo.texture;
      const reel = this.reelRefs.current[reelIndex];
      const controller = reel?.getReelController();

      if (!controller?.spritesheet?.textures) {
        return { texture: null, scale: 1, symbolId: WILD_SYMBOL_ID };
      }

      // Get the wild texture from spritesheet
      const wildTexture = controller.spritesheet.textures[`${textureName}.png`];

      if (!wildTexture) {
        return { texture: null, scale: 1, symbolId: WILD_SYMBOL_ID };
      }

      // Get scale from any visible sprite
      const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
      const spriteData = controller.spinningSprites?.[spriteIndex];
      const scale = spriteData?.normalSprite?.scale?.x || 1;

      return {
        texture: wildTexture,
        scale: scale,
        symbolId: WILD_SYMBOL_ID,
      };
    }

    // Read symbol data directly from the rendered sprite on screen
    // This ensures we always show info for what's actually visible, not from Redux state
    const reel = this.reelRefs.current[reelIndex];
    const controller = reel?.getReelController();

    if (!controller?.spinningSprites) {
      return { texture: null, scale: 1, symbolId: null };
    }

    // Visible sprites start at VISIBLE_SYMBOL_START_INDEX (matches ReelControllerV2)
    const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
    const spriteData = controller.spinningSprites[spriteIndex];

    if (!spriteData?.normalSprite) {
      return { texture: null, scale: 1, symbolId: null };
    }

    // Get the actual symbol ID from the rendered sprite (not from Redux)
    const symbolId = spriteData.currentSymbolId;

    if (symbolId === undefined || symbolId === null) {
      return { texture: null, scale: 1, symbolId: null };
    }

    // Get symbol info from SymbolData
    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo) {
      return { texture: null, scale: 1, symbolId: null };
    }

    // Check if symbol has texture (blank symbols don't)
    const textureName = symbolInfo.texture;
    if (!textureName || textureName === null) {
      return { texture: null, scale: 1, symbolId };
    }

    return {
      texture: spriteData.normalSprite.texture,
      scale: spriteData.normalSprite.scale?.x || 1,
      symbolId: symbolId,
      coinValueText: spriteData.valueText?.text || null, // Get the actual coin value from the reel
    };
  }

  /**
   * Creates text content based on symbol type
   * Returns description text for wild/scatter/coin, or paytable entries for regular symbols
   */
  _createTextContent(symbolId, isLeftSide, popupWidth, popupHeight) {
    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo) return [];

    const state = store.getState();
    const paytable = state.game.paytable;
    const bet = state.game.bet;
    const currency = state.game.currency;
    const { symbolScale } = this.dimensions;

    // Determine symbol type
    const symbolType = this._getSymbolType(symbolId, symbolInfo);

    // Calculate text container position
    const textContainerWidth =
      this.dimensions.symbolWidth *
      INFO_SYMBOL_POPUP_CONFIG.TEXT_CONTAINER_WIDTH_MULTIPLIER;
    const textContainerX = isLeftSide
      ? this.dimensions.symbolWidth
      : -this.dimensions.symbolWidth;

    if (symbolType === "wild") {
      return this._createDescriptionText(
        this.t("wild_minipaytable", { ns: "common" }),
        textContainerX,
        textContainerWidth,
        symbolScale,
      );
    }

    if (symbolType === "scatter") {
      return this._createDescriptionText(
        this.t("scatter_minipaytable", { ns: "common" }),
        textContainerX,
        textContainerWidth,
        symbolScale,
      );
    }

    if (symbolType === "coin") {
      return this._createDescriptionText(
        this.t("coin_minipaytable", { ns: "common" }),
        textContainerX,
        textContainerWidth,
        symbolScale,
      );
    }

    // Regular symbol - create paytable
    return this._createPaytableText(
      symbolId,
      paytable,
      bet,
      currency,
      textContainerX,
      textContainerWidth,
      symbolScale,
      isLeftSide,
    );
  }

  /**
   * Determines symbol type from symbolPay field
   * Returns: "wild", "scatter", "coin" (symbolPay starts with "c"), or "regular"
   * Cached for performance
   */
  _getSymbolType(symbolId, symbolInfo) {
    if (!symbolInfo || !symbolInfo.symbolPay) return "regular";

    // Check cache first
    if (this._symbolTypeCache.has(symbolId)) {
      return this._symbolTypeCache.get(symbolId);
    }

    const symbolPay = symbolInfo.symbolPay;
    let symbolType = "regular";

    // Check symbol type based on symbolPay field
    if (symbolPay === "wild") {
      symbolType = "wild";
    } else if (symbolPay === "scatter") {
      symbolType = "scatter";
    } else if (symbolPay.startsWith("c")) {
      // Coin symbols have symbolPay starting with "c" (c1, c2, c3, etc.)
      symbolType = "coin";
    }
    // Everything else is regular (hp, mp, lp)

    // Cache result
    this._symbolTypeCache.set(symbolId, symbolType);

    return symbolType;
  }

  /**
   * Builds a PIXI TextStyle config using the SECONDARY preset.
   * Text is always rendered at a fixed native size (container scaling
   * handles responsive sizing), so stroke/shadow use SECONDARY values directly.
   */
  _getPrimaryStyle(fontSize, extraProps = {}) {
    const secondary = PixiSettings.FONT_STYLES.SECONDARY;

    return {
      fontFamily: secondary.fontFamily,
      fontSize,
      fontWeight: "300",
      letterSpacing: 0.4,
      fill: secondary.fill,
      fillGradientType: secondary.fillGradientType,
      stroke: secondary.stroke,
      strokeThickness: secondary.strokeThickness,
      dropShadow: secondary.dropShadow,
      dropShadowColor: secondary.dropShadowColor,
      dropShadowDistance: secondary.dropShadowDistance - 1,
      dropShadowBlur: secondary.dropShadowBlur,
      dropShadowAlpha: secondary.dropShadowAlpha,
      dropShadowAngle: secondary.dropShadowAngle,
      ...extraProps,
    };
  }

  /**
   * Creates description text for wild/scatter/coin symbols.
   * Text renders at a fixed native size inside a container scaled by symbolScale,
   * ensuring identical visual quality across all screen sizes.
   */
  _createDescriptionText(
    text,
    textContainerX,
    textContainerWidth,
    symbolScale,
  ) {
    const baseFontSize = INFO_SYMBOL_POPUP_CONFIG.DESCRIPTION_FONT_SIZE;

    // Word wrap width is in unscaled coordinates (container handles scaling)
    const style = this._getPrimaryStyle(baseFontSize, {
      align: "center",
      wordWrap: true,
      wordWrapWidth: Math.round(textContainerWidth / symbolScale),
    });

    const textSprite = new PIXI.Text(text, style);
    textSprite.anchor.set(0.5);
    textSprite.resolution = 2;
    textSprite.name = "info_popup_description";

    // Wrap in a scaled container so text looks identical everywhere
    const textContainer = new PIXI.Container();
    textContainer.name = "description_text_container";
    textContainer.scale.set(symbolScale);
    textContainer.x = Math.round(textContainerX);
    textContainer.y = 0;
    textContainer.addChild(textSprite);

    return [textContainer];
  }

  /**
   * Creates paytable text entries for regular symbols.
   * Text renders at a fixed native size inside a container scaled by symbolScale,
   * ensuring identical visual quality across all screen sizes.
   * Format: "5: 1.00 FUN" (symbol count : formatted payout currency)
   */
  _createPaytableText(
    symbolId,
    paytable,
    bet,
    currency,
    textContainerX,
    textContainerWidth,
    symbolScale,
    isLeftSide,
  ) {
    if (!paytable || !paytable[symbolId]) return [];

    // Build display entries
    const displayEntries = paytable[symbolId]
      .map((value, idx) => ({ value, originalIndex: idx }))
      .reverse()
      .filter((item) => item.value !== 0)
      .map((item, displayIndex) => {
        const symbolCount = paytable[symbolId].length - displayIndex;
        const payoutAmount = item.value * (bet / this.baseBet);
        const formattedPayout = formatToCustomLocale(payoutAmount);
        return { symbolCount, formattedPayout };
      });

    if (displayEntries.length === 0) return [];

    // Use fixed base font size (container scaling handles responsiveness)
    const baseFontSize = INFO_SYMBOL_POPUP_CONFIG.PAYTABLE_FONT_SIZE;
    const lineHeight =
      baseFontSize * INFO_SYMBOL_POPUP_CONFIG.LINE_HEIGHT_MULTIPLIER;

    // Available width in unscaled coordinates
    const unscaledContainerWidth = Math.round(textContainerWidth / symbolScale);

    // Calculate scale factor to fit text in unscaled space
    const paytableScaleFactor = this._calculatePaytableScaleFactor(
      displayEntries,
      baseFontSize,
      unscaledContainerWidth,
      currency,
    );

    const adjustedFontSize = Math.round(baseFontSize * paytableScaleFactor);
    const adjustedGap =
      adjustedFontSize * INFO_SYMBOL_POPUP_CONFIG.TEXT_GAP_MULTIPLIER;

    // Shared gradient style
    const lineStyle = this._getPrimaryStyle(adjustedFontSize, {
      align: "left",
    });

    // Calculate vertical positioning in unscaled coordinates
    const totalContentHeight = displayEntries.length * lineHeight;
    const startY = -totalContentHeight / 2;
    const offsetMultiplier = isLeftSide
      ? INFO_SYMBOL_POPUP_CONFIG.TEXT_START_X_OFFSET_MULTIPLIER_LEFT
      : INFO_SYMBOL_POPUP_CONFIG.TEXT_START_X_OFFSET_MULTIPLIER_RIGHT;
    // textContainerX and textContainerWidth are in scaled space,
    // convert to unscaled for positioning inside the container
    const unscaledTextContainerX = Math.round(textContainerX / symbolScale);
    const textStartX =
      unscaledTextContainerX - unscaledContainerWidth * offsetMultiplier;

    // Create text sprites inside a scaled container
    const textContainer = new PIXI.Container();
    textContainer.name = "paytable_text_container";
    textContainer.scale.set(symbolScale);

    displayEntries.forEach((entry, displayIndex) => {
      const lineY = startY + displayIndex * lineHeight;
      const symbolCountText = `${entry.symbolCount}x`;
      const payoutText = `${entry.formattedPayout} ${currency || ""}`.trim();

      // Create symbol count text
      const symbolCountSprite = new PIXI.Text(symbolCountText, lineStyle);
      symbolCountSprite.x = Math.round(textStartX);
      symbolCountSprite.y = Math.round(lineY);
      symbolCountSprite.resolution = 2;
      symbolCountSprite.name = `paytable_symbol_count_${displayIndex}`;
      textContainer.addChild(symbolCountSprite);

      // Create payout text
      const symbolCountWidth = symbolCountSprite.width;
      const payoutX = Math.round(textStartX + symbolCountWidth + adjustedGap);
      const payoutSprite = new PIXI.Text(payoutText, lineStyle);
      payoutSprite.x = payoutX;
      payoutSprite.y = Math.round(lineY);
      payoutSprite.resolution = 2;
      payoutSprite.name = `paytable_payout_${displayIndex}`;
      textContainer.addChild(payoutSprite);
    });

    return [textContainer];
  }

  /**
   * Calculates scale factor to fit paytable text within container width
   * Scales down font size if text would overflow
   * Cached for performance - invalidated when dimensions or entries change
   */
  _calculatePaytableScaleFactor(
    displayEntries,
    fontSize,
    availableWidth,
    currency,
  ) {
    // Create cache key from inputs (include payout text so bet changes trigger recalculation)
    const payoutKeys = displayEntries.map((e) => e.formattedPayout).join("_");
    const cacheKey = `${displayEntries.length}_${fontSize}_${availableWidth}_${currency}_${payoutKeys}`;

    // Check cache first
    if (this._paytableScaleFactorCache.has(cacheKey)) {
      return this._paytableScaleFactorCache.get(cacheKey);
    }

    const baseStyle = new PIXI.TextStyle(this._getPrimaryStyle(fontSize));

    let minScaleFactor = 1;

    displayEntries.forEach((entry) => {
      const symbolCountText = `${entry.symbolCount}x`;
      const payoutText = `${entry.formattedPayout} ${currency || ""}`.trim();

      const symbolCountWidth = PIXI.TextMetrics.measureText(
        symbolCountText,
        baseStyle,
      ).width;
      const payoutWidth = PIXI.TextMetrics.measureText(
        payoutText,
        baseStyle,
      ).width;
      const gap = fontSize * INFO_SYMBOL_POPUP_CONFIG.TEXT_GAP_MULTIPLIER;
      const totalTextWidth = symbolCountWidth + gap + payoutWidth;

      if (totalTextWidth > availableWidth) {
        const scaleFactor = availableWidth / totalTextWidth;
        minScaleFactor = Math.min(minScaleFactor, scaleFactor);
      }
    });

    // Cache result
    this._paytableScaleFactorCache.set(cacheKey, minScaleFactor);

    return minScaleFactor;
  }

  /**
   * Updates clickable area positions and sizes on resize
   * Maintains grid alignment with symbol positions
   */
  _updateClickableAreaPositions() {
    const { symbolWidth, symbolHeight, reelSpacing } = this.dimensions;

    let areaIndex = 0;
    for (let reelIndex = 0; reelIndex < Settings.REEL_COUNT; reelIndex++) {
      for (
        let symbolIndex = 0;
        symbolIndex < Settings.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        const clickArea = this.clickableAreas[areaIndex];
        if (clickArea) {
          clickArea.width = symbolWidth;
          clickArea.height = symbolHeight;
          clickArea.x = Math.round(reelSpacing * (reelIndex - 2));
          clickArea.y = Math.round(
            symbolHeight * symbolIndex +
              symbolHeight *
                INFO_SYMBOL_POPUP_CONFIG.SYMBOL_Y_OFFSET_MULTIPLIER,
          );
        }
        areaIndex++;
      }
    }
  }

  /**
   * Handles resize - updates clickable areas and recreates active popup
   * Ensures popup maintains correct position and scale after dimension changes
   */
  resize(newDimensions) {
    this.dimensions = newDimensions;
    this.device = newDimensions.device;

    // Invalidate caches when dimensions change
    this._framePositionCache = null;
    this._lastFrameDimensions = null;
    this._paytableScaleFactorCache.clear(); // Clear paytable cache on resize

    // Update clickable areas
    this._updateClickableAreaPositions();

    // Recreate active popup if exists
    if (this.activePopup) {
      const { reelIndex, symbolIndex } = this.activePopup;
      this.openPopup(reelIndex, symbolIndex);
    }
  }

  /**
   * Sets interactive state of clickable areas
   * Disables interaction during spin, autoplay, Hold & Win, or win animations
   */
  setInteractive(interactive) {
    this.clickableAreas.forEach((area) => {
      area.eventMode = interactive ? "static" : "none";
    });
  }

  /**
   * Checks if current device is desktop
   * @returns {boolean} True if desktop
   */
  isDesktop() {
    return this.device === PixiSettings.DEVICE.DESKTOP;
  }

  /**
   * Checks if popup is currently active
   * @returns {boolean} True if popup is open
   */
  hasActivePopup() {
    return !!this.activePopup;
  }

  /**
   * Updates bet value and refreshes popup if open
   * For coin symbols: Don't refresh (keep original values from completed spin)
   * For other symbols: Refresh with new bet-multiplied paytable values
   */
  updateBet(newBet) {
    this.bet = newBet || 1;

    // If popup is open, check if it's a coin symbol
    if (this.activePopup) {
      const { symbolId } = this.activePopup;

      // If it's a coin symbol, don't refresh
      // Coin values should remain frozen at their original values from the completed spin
      if (isCoinSymbol(symbolId)) {
        return; // Skip refresh for coins
      }

      // For non-coin symbols (paytable entries), refresh with new bet values
      const { reelIndex, symbolIndex } = this.activePopup;
      this.closePopup();
      this.openPopup(reelIndex, symbolIndex);
    }
  }

  /**
   * Updates base bet value and refreshes popup if open
   * Recreates popup with new paytable calculations (paytable_value * (bet / baseBet))
   */
  updateBaseBet(newBaseBet) {
    this.baseBet = newBaseBet || 1;

    // If popup is open, recreate it with new base bet values
    if (this.activePopup) {
      const { reelIndex, symbolIndex } = this.activePopup;
      this.closePopup();
      this.openPopup(reelIndex, symbolIndex);
    }
  }

  /**
   * Calculates display value for coin symbols (coinValue * bet) with locale formatting
   * @param {string} symbolValue - Coin value from SymbolData (e.g., "1", "2", "5")
   * @returns {string} Formatted display value (e.g., "1.00", "2.00") or empty string if invalid
   */
  _getCoinDisplayValue(symbolValue) {
    if (!symbolValue || symbolValue === null) {
      return "";
    }

    try {
      const coinValue = parseInt(symbolValue, 10);

      if (isNaN(coinValue) || coinValue <= 0) {
        return "";
      }

      const displayValue = coinValue * this.bet;
      return formatToCustomLocale(displayValue);
    } catch (error) {
      return "";
    }
  }

  /**
   * Cleanup - destroys all sprites and removes event listeners
   * Called when component unmounts
   */
  destroy() {
    this.closePopup();
    this.clickableAreas.forEach((area) => {
      area.removeAllListeners();
      area.destroy();
    });
    this.clickableAreas = [];
  }
}

export default InfoOverlayController;
